import { OAuthProvider } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Settings } from 'lucide-react';
import { ComponentProps, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { OAuthConnectionInformation } from '../../integrations/types/types';
import { IS_XERO_CARD_HIDDEN_KEY } from '../../payments/components/payments-flow-modal';
import { useExcludeSender } from '../hooks/use-exclude-sender';
import { getDateRangeValues } from '../utils/get-date-range-values';
import { EmailConnectionModal } from './email-connection-modal/email-connection-modal';
import { OAuthProviderDisconnectConfirmationModal } from './invoice-data-section/modals/oauth-provider-disconnect-confirmation-modal';
import { SettingsModal } from './invoice-data-section/modals/settings-modal';
import { XeroConnectModal } from './invoice-data-section/modals/xero/xero-connect-modal';
import { XeroConnectedModal } from './invoice-data-section/modals/xero/xero-connected-modal';
import { OAuthConnectionBadge } from './oauth-connection-badge';

const CONNECT_EMAIL_URL = '/api/oauth/authorize?provider=gmail&redirect=true';
// Exported for use in integrations page
export const CONNECT_XERO_URL = '/api/oauth/authorize?provider=xero&redirect=true';

const XERO_SUCCESS_MODAL_SEEN_KEY = 'xero_success_modal_seen';
const XERO_SUCCESS_MODAL_SEEN_VALUE = 'true';
const XERO_FAILED_TOAST_SEEN_KEY = 'xero_failed_toast_seen';
const XERO_FAILED_TOAST_SEEN_VALUE = 'true';
const QUERY_PARAM_KEY_OAUTH_PROVIDER = 'provider';
const QUERY_PARAM_KEY_OAUTH_PROVIDER_CONNECTION_STATUS = 'success';
const QUERY_PARAM_VALUE_OAUTH_PROVIDER_XERO = 'xero';
const QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_SUCCESS = 'true';
const QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_FAILED = 'false';

const VALID_EMAIL_MODAL_VALUES = ['empty-state', 'integrations'];

type EntryPoint = 'settings-modal' | 'invoices-page' | 'integrations-page';

type EmailConnectionStatusResponse = { connections: { excludedSenders: string[] }[]; totalCount: number };

type OAuthConnectionConfig =
  | {
      type: OAuthProvider.GMAIL;
      connectionStatus: OAuthConnectionInformation | null;
      isChecking: boolean;
      isDisconnecting: boolean;
      onDisconnect: (connectionId: string) => Promise<void>;
      onConnect: () => void;
    }
  | {
      type: OAuthProvider.XERO;
      connectionStatus: OAuthConnectionInformation | null;
      isChecking: boolean;
      isDisconnecting: boolean;
      onDisconnect: (connectionId: string) => Promise<void>;
    };

type EmailConnectionStatusConfig = {
  connectionStatus: EmailConnectionStatusResponse;
  mutateConnectionStatus: () => Promise<void>;
};

type ConnectionsAndSettingsSectionProps = {
  gmailConfig: OAuthConnectionConfig;
  xeroConfig: OAuthConnectionConfig;
  emailConnectionStatusConfig: EmailConnectionStatusConfig;
  onDeleteEvidence: (evidenceIds: string[]) => Promise<void>;
};

export const ConnectionsAndSettingsSection = ({
  gmailConfig,
  xeroConfig,
  emailConnectionStatusConfig,
  onDeleteEvidence,
}: ConnectionsAndSettingsSectionProps) => {
  const {
    connectionStatus: gmailConnectionStatus,
    isChecking: isCheckingGmailConnection,
    isDisconnecting: isDisconnectingGmail,
    onDisconnect: onDisconnectGmail,
  } = gmailConfig;
  const {
    connectionStatus: xeroConnectionStatus,
    isChecking: isCheckingXeroConnection,
    isDisconnecting: isDisconnectingXero,
    onDisconnect: onDisconnectXero,
  } = xeroConfig;
  const { connectionStatus: emailConnectionStatus, mutateConnectionStatus: mutateEmailConnectionStatus } =
    emailConnectionStatusConfig;
  const { isXeroFlowLive, isLongXeroFlowLive } = useFlags<{ isXeroFlowLive: boolean; isLongXeroFlowLive: boolean }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const isEmailModalOpen = searchParams.get('email-modal-open');
  const [isEmailConnectionModalOpen, setIsEmailConnectionModalOpen] = useState<
    | { isOpen: false }
    | {
        isOpen: true;
        entryPoint: EntryPoint;
      }
  >(
    isEmailModalOpen && VALID_EMAIL_MODAL_VALUES.includes(isEmailModalOpen)
      ? { isOpen: true, entryPoint: isEmailModalOpen === 'empty-state' ? 'invoices-page' : 'integrations-page' }
      : { isOpen: false },
  );
  const [isXeroConnectionModalOpen, setIsXeroConnectionModalOpen] = useState<
    | { isOpen: false }
    | {
        isOpen: true;
        entryPoint: Exclude<EntryPoint, 'integrations-page'>;
      }
  >({
    isOpen: false,
  });
  const [isEmailDisconnectConfirmationModalOpen, setIsEmailDisconnectConfirmationModalOpen] = useState(false);
  const [isXeroDisconnectConfirmationModalOpen, setIsXeroDisconnectConfirmationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isXeroConnectionProvider =
    searchParams.get(QUERY_PARAM_KEY_OAUTH_PROVIDER) === QUERY_PARAM_VALUE_OAUTH_PROVIDER_XERO;
  const isConnectionSuccess =
    searchParams.get(QUERY_PARAM_KEY_OAUTH_PROVIDER_CONNECTION_STATUS) ===
    QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_SUCCESS;
  const isConnectionFailed =
    searchParams.get(QUERY_PARAM_KEY_OAUTH_PROVIDER_CONNECTION_STATUS) ===
    QUERY_PARAM_VALUE_OAUTH_PROVIDER_CONNECTION_STATUS_FAILED;

  const isXeroSuccessModalSeen = localStorage.getItem(XERO_SUCCESS_MODAL_SEEN_KEY) === XERO_SUCCESS_MODAL_SEEN_VALUE;

  const [isXeroConnectionSuccessModalOpen, setIsXeroConnectionSuccessModalOpen] = useState(
    isXeroConnectionProvider && isConnectionSuccess && !isXeroSuccessModalSeen,
  );

  const { excludeSender, isExcludingSender } = useExcludeSender();

  useEffect(() => {
    if (
      isXeroConnectionProvider &&
      isConnectionFailed &&
      localStorage.getItem(XERO_FAILED_TOAST_SEEN_KEY) !== XERO_FAILED_TOAST_SEEN_VALUE
    ) {
      localStorage.setItem(XERO_FAILED_TOAST_SEEN_KEY, 'true');

      toast.error('Failed to connect to Xero. Please try again and contact us if the issue persists.');
    }
  }, [isConnectionFailed, isXeroConnectionProvider]);

  const redirectUrlQueryParam = `redirectUrl=${encodeURIComponent(window.location.pathname)}`;

  const onConnectEmail = async (
    values: Parameters<ComponentProps<typeof EmailConnectionModal>['onSelectDateRange']>[0],
  ) => {
    setIsLoading(true);

    const dateRangeValues = getDateRangeValues(values.dateRange);

    if (!dateRangeValues) {
      window.location.href = `${CONNECT_EMAIL_URL}&${redirectUrlQueryParam}`;

      return;
    }

    const { startDate, endDate } = dateRangeValues;

    window.location.href = `${CONNECT_EMAIL_URL}&startDate=${startDate}&endDate=${endDate}&${redirectUrlQueryParam}`;
  };

  const onConnectXero = async () => {
    window.location.href = `${CONNECT_XERO_URL}&${redirectUrlQueryParam}`;

    return;
  };

  const emailConnectionConfig = gmailConnectionStatus?.connectionId
    ? {
        isConnected: true as const,
        email: gmailConnectionStatus.email ?? 'Gmail Connection Active',
        onDisconnect: async () => {
          setIsSettingsModalOpen(false);

          setIsEmailDisconnectConfirmationModalOpen(true);
        },
      }
    : {
        isConnected: false as const,
        onConnect: () => {
          setIsSettingsModalOpen(false);

          setIsEmailConnectionModalOpen({ isOpen: true, entryPoint: 'settings-modal' });
        },
      };

  const xeroConnectionConfig = xeroConnectionStatus?.connectionId
    ? {
        isConnected: true as const,
        email: xeroConnectionStatus.email ?? 'Xero Connection Active',
        onDisconnect: async () => {
          setIsSettingsModalOpen(false);

          setIsXeroDisconnectConfirmationModalOpen(true);

          localStorage.removeItem(XERO_SUCCESS_MODAL_SEEN_KEY);
          localStorage.removeItem(IS_XERO_CARD_HIDDEN_KEY);
        },
      }
    : {
        isConnected: false as const,
        onConnect: () => {
          setIsSettingsModalOpen(false);

          localStorage.removeItem(XERO_FAILED_TOAST_SEEN_KEY);
          setIsXeroConnectionModalOpen({ isOpen: true, entryPoint: 'settings-modal' });
        },
      };

  return (
    <>
      <Inline space={2}>
        <OAuthConnectionBadge
          provider={OAuthProvider.GMAIL}
          isConnected={!!gmailConnectionStatus?.connectionId}
          isLoading={isDisconnectingGmail || isCheckingGmailConnection || isLoading}
          onConnect={() => setIsEmailConnectionModalOpen({ isOpen: true, entryPoint: 'invoices-page' })}
        />

        {isXeroFlowLive && !isLongXeroFlowLive && (
          <OAuthConnectionBadge
            provider={OAuthProvider.XERO}
            isConnected={!!xeroConnectionStatus?.connectionId}
            isLoading={isCheckingXeroConnection || isDisconnectingXero}
            onConnect={() => {
              localStorage.removeItem(XERO_FAILED_TOAST_SEEN_KEY);

              setIsXeroConnectionModalOpen({ isOpen: true, entryPoint: 'invoices-page' });
            }}
          />
        )}

        <Button
          variant="ghost"
          size="icon"
          aria-label="Open invoice settings"
          onClick={() => setIsSettingsModalOpen(true)}
        >
          <div className="h-9 w-9 rounded-full bg-muted p-2.5">
            <Settings strokeWidth={1.5} className="h-full w-full" />
          </div>
        </Button>
      </Inline>

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        excludedSendersConfig={{
          isLoading: isExcludingSender,
          excludedSenders: emailConnectionStatus.connections[0]?.excludedSenders ?? [],
          onSubmit: async (excludedSenders) => {
            if (!gmailConnectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to exclude sender list', {
                extra: {
                  gmailConnectionStatus,
                },
              });

              return;
            }

            try {
              const { deletedEvidenceIds } = await excludeSender({
                urlSuffix: `/${gmailConnectionStatus.connectionId}/excluded-senders`,
                excludedSenders,
                mode: 'replace',
              });

              await mutateEmailConnectionStatus();
              await onDeleteEvidence(deletedEvidenceIds);

              toast.success('Sender excluded', {
                description: `We won't pull in any more invoices from these email addresses.`,
              });
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to exclude senders');
            }
          },
        }}
        emailConnectionConfig={emailConnectionConfig}
        xeroConnectionConfig={xeroConnectionConfig}
      />

      <EmailConnectionModal
        isOpen={isEmailConnectionModalOpen.isOpen}
        onClose={() => {
          setIsEmailConnectionModalOpen({ isOpen: false });
          setSearchParams({});

          if (isEmailConnectionModalOpen.isOpen && isEmailConnectionModalOpen.entryPoint === 'settings-modal') {
            setIsSettingsModalOpen(true);
          }

          if (isEmailConnectionModalOpen.isOpen && isEmailConnectionModalOpen.entryPoint === 'integrations-page') {
            navigate('/integrations');
          }
        }}
        onSelectDateRange={onConnectEmail}
      />

      <XeroConnectModal
        isOpen={isXeroConnectionModalOpen.isOpen}
        onClose={() => {
          setIsXeroConnectionModalOpen({ isOpen: false });

          if (isXeroConnectionModalOpen.isOpen && isXeroConnectionModalOpen.entryPoint === 'settings-modal') {
            setIsSettingsModalOpen(true);
          }
        }}
        onContinue={onConnectXero}
      />

      <XeroConnectedModal
        isOpen={isXeroConnectionSuccessModalOpen}
        onClose={() => {
          setIsXeroConnectionSuccessModalOpen(false);

          localStorage.setItem(XERO_SUCCESS_MODAL_SEEN_KEY, XERO_SUCCESS_MODAL_SEEN_VALUE);
        }}
      />

      <OAuthProviderDisconnectConfirmationModal
        provider={OAuthProvider.GMAIL}
        isOpen={isEmailDisconnectConfirmationModalOpen}
        isLoading={isDisconnectingGmail}
        onClose={() => {
          setIsEmailDisconnectConfirmationModalOpen(false);

          setIsSettingsModalOpen(true);
        }}
        onDisconnect={async () => {
          if (!gmailConnectionStatus?.connectionId) {
            Sentry.captureMessage('No connection id found when trying to disconnect email', {
              extra: {
                gmailConnectionStatus,
              },
            });

            return;
          }

          await onDisconnectGmail(gmailConnectionStatus.connectionId);

          setIsEmailDisconnectConfirmationModalOpen(false);
        }}
      />

      <OAuthProviderDisconnectConfirmationModal
        provider={OAuthProvider.XERO}
        isOpen={isXeroDisconnectConfirmationModalOpen}
        isLoading={isDisconnectingXero}
        onClose={() => {
          setIsXeroDisconnectConfirmationModalOpen(false);

          setIsSettingsModalOpen(true);
        }}
        onDisconnect={async () => {
          if (!xeroConnectionStatus?.connectionId) {
            Sentry.captureMessage('No connection id found when trying to disconnect xero', {
              extra: {
                xeroConnectionStatus,
              },
            });

            return;
          }

          await onDisconnectXero(xeroConnectionStatus.connectionId);

          setIsXeroDisconnectConfirmationModalOpen(false);
        }}
      />
    </>
  );
};
